/**
 * useGazeNavigation.js
 * =====================
 * React hook that converts raw gaze predictions (from useGazeDetection)
 * into deliberate UI navigation events — mimicking what a finger tap does
 * in the current click-based app.
 *
 * The core challenge: GazeNet predicts one direction per frame. Users will
 * naturally glance in a direction before committing to it, and predictions
 * are noisy. We need to distinguish "I'm intentionally looking at this
 * quadrant" from "my gaze passed through it briefly."
 *
 * Solution: Temporal smoothing with a rolling window.
 *   - Accumulate the last N predictions
 *   - Only fire a selection when one direction appears in > THRESHOLD % of frames
 *   - After firing, enforce a cooldown so the same selection can't immediately re-fire
 *   - "Straight" (looking at camera) resets the accumulator → natural neutral state
 *
 * Usage:
 *   import { useGazeNavigation } from './useGazeNavigation';
 *
 *   function HomePage() {
 *     const { gazeDirection, videoRef, isReady } = useGazeNavigation({
 *       enabled: true,
 *       onGazeSelect: (direction) => {
 *         // direction: 'up' | 'down' | 'left' | 'right'
 *         // Map to your quadrant and call handleClick()
 *         const directionToIndex = { up: 0, left: 1, right: 2, down: 3 };
 *         const idx = directionToIndex[direction];
 *         if (idx !== undefined && items[idx]) handleClick(items[idx]);
 *       },
 *     });
 *
 *     // Render a hidden video element for the webcam feed
 *     return (
 *       <>
 *         <video ref={videoRef} style={{ display: 'none' }} playsInline muted />
 *         ... rest of your UI ...
 *       </>
 *     );
 *   }
 */

import { useEffect, useRef, useState } from 'react';
import { useGazeDetection } from './useGazeDetection';

// ─────────────────────────────────────────────────────────────────────────────
// Temporal smoothing configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Number of recent predictions to keep in the rolling window.
 * At 10 fps (INFERENCE_FPS in useGazeDetection), 8 frames = ~800ms of history.
 * The user needs to sustain their gaze for most of this window to trigger selection.
 */
const WINDOW_SIZE = 10;

/**
 * Fraction of frames that must agree before the quadrant highlight appears.
 * Lower than CONFIDENCE_THRESHOLD so the user gets early visual feedback
 * that the model is detecting their gaze before the selection actually fires.
 * 0.60 = 60% of the current window must agree to show the glow.
 */
const HIGHLIGHT_THRESHOLD = 0.40;

/**
 * Fraction of frames in the window that must show the same direction
 * before we fire a navigation event.
 * 0.70 = 70% of 8 frames = at least 6 frames must agree.
 * This means the user must hold a consistent direction for ~600ms.
 */
const CONFIDENCE_THRESHOLD = 0.70;

/**
 * Minimum number of predictions in the window before we attempt classification.
 * Set to 2 so the highlight can appear quickly (after ~200ms).
 * Must be <= WINDOW_SIZE.
 */
const MIN_WINDOW_FILL = 2;

/**
 * Cooldown in milliseconds after a selection fires.
 * During cooldown, no new selection can fire even if the gaze is consistent.
 * This gives the user time to return to a neutral gaze position.
 * 1500ms = 1.5 seconds.
 */
const COOLDOWN_MS = 1500;

/**
 * Directions that can trigger navigation events.
 * 'straight' is intentionally excluded — it's the neutral resting state.
 */
const NAVIGABLE_DIRECTIONS = new Set(['up', 'down', 'left', 'right']);


// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * useGazeNavigation
 *
 * @param {object}   options
 * @param {boolean}  options.enabled       Pass false to disable gaze entirely.
 * @param {function} options.onGazeSelect  Callback: (direction: string) => void
 *                                         Called with 'up'|'down'|'left'|'right'
 *                                         when a sustained gaze is detected.
 * @param {number}   options.windowSize    Override the rolling-window size.
 *                                         Defaults to WINDOW_SIZE (10).
 *                                         Driven by GazeConfigContext speedStep via
 *                                         GazeConfigContext.windowSize.
 *
 * @returns {object}
 *   videoRef      — attach to a hidden <video> element
 *   isReady       — true once camera + MediaPipe are running
 *   activeDirection — the direction currently being accumulated (or null)
 *                     Use this to show a visual gaze indicator in the UI
 *   fillLevel       — 0.0–1.0 how full the current window is (for progress ring UI)
 *   error           — error message string, or null
 */
export function useGazeNavigation({ enabled = true, onGazeSelect, windowSize = WINDOW_SIZE, calibration = null } = {}) {

  // ── Get raw predictions from the detection hook ────────────────────────
  // useGazeDetection handles MediaPipe + webcam + API calls.
  // We consume its output here and add the temporal smoothing layer.
  const {
    videoRef,
    isReady,
    gazeDirection,      // latest raw prediction: 'up'|'down'|'left'|'right'|'straight'|null
    confidence,         // latest prediction confidence
    irisVerticalRatio,  // current frame's IVR (pass through for calibration use)
    doubleBlinkCount,   // increments each time a double blink is detected
    error,
  } = useGazeDetection({ enabled, calibration });


  // ── State ────────────────────────────────────────────────────────────────
  // activeDirection: the direction that is currently accumulating.
  // null means no direction is dominant in the current window.
  const [activeDirection, setActiveDirection] = useState(null);

  // fillLevel: what fraction of WINDOW_SIZE predictions agree on activeDirection.
  // Used to drive a visual "filling ring" in the UI so the user knows
  // a selection is building up.
  const [fillLevel, setFillLevel] = useState(0);


  // ── Refs ─────────────────────────────────────────────────────────────────
  // The rolling window of recent predictions.
  // Using a ref (not state) because we don't want array updates to cause re-renders.
  const windowRef = useRef([]);

  // Cooldown tracking: timestamp of the last fired selection.
  const lastFiredRef = useRef(0);

  // Stable ref for the onGazeSelect callback.
  // Wrapping in a ref means useEffect doesn't need onGazeSelect in its dependency array.
  const onGazeSelectRef = useRef(onGazeSelect);
  useEffect(() => {
    onGazeSelectRef.current = onGazeSelect;
  }, [onGazeSelect]);

  // ── Trim window when windowSize decreases ─────────────────────────────────
  // If the user moves the speed slider to a smaller window, trim the buffer
  // immediately so stale frames don't delay the new, tighter threshold.
  useEffect(() => {
    while (windowRef.current.length > windowSize) {
      windowRef.current.shift();
    }
  }, [windowSize]);


  // ── Process each new prediction ──────────────────────────────────────────
  useEffect(() => {
    // Skip null predictions (before the first API response)
    if (gazeDirection === null) return;

    // ── Update the rolling window ─────────────────────────────────────────
    // Push the new prediction into the window array
    windowRef.current.push(gazeDirection);

    // Trim to windowSize by removing the oldest entry.
    // windowSize is the prop-driven value; WINDOW_SIZE is the static default.
    if (windowRef.current.length > windowSize) {
      windowRef.current.shift();  // remove oldest prediction (front of array)
    }

    const window = windowRef.current;
    const windowFilled = window.length >= MIN_WINDOW_FILL;

    // ── "Straight" resets the accumulator ────────────────────────────────
    // When the user looks at the camera (straight), we treat it as a reset signal.
    // Clear the window so the next directional gaze starts fresh.
    // This prevents "momentum" from a previous direction bleeding into the next.
    if (gazeDirection === 'straight') {
      windowRef.current = [];
      setActiveDirection(null);
      setFillLevel(0);
      return;
    }

    // ── Check cooldown ────────────────────────────────────────────────────
    // If we recently fired a selection, don't process until cooldown expires.
    const now = Date.now();
    const inCooldown = (now - lastFiredRef.current) < COOLDOWN_MS;
    if (inCooldown) {
      // During cooldown, show a "dimmed" state by clearing activeDirection
      setActiveDirection(null);
      setFillLevel(0);
      return;
    }

    // ── Count votes in the window ─────────────────────────────────────────
    // Count how many of the recent predictions match each direction.
    // We only count directions that can trigger navigation (exclude 'straight').
    const voteCounts = {};
    for (const direction of window) {
      if (NAVIGABLE_DIRECTIONS.has(direction)) {
        voteCounts[direction] = (voteCounts[direction] || 0) + 1;
      }
    }

    // Find the direction with the most votes
    let dominantDirection = null;
    let dominantCount = 0;
    for (const [dir, count] of Object.entries(voteCounts)) {
      if (count > dominantCount) {
        dominantCount = count;
        dominantDirection = dir;
      }
    }

    // ── Update UI fill indicator ──────────────────────────────────────────
    // Show highlight at 60% agreement — earlier feedback than the 70% fire threshold
    if (dominantDirection && windowFilled && dominantCount / window.length >= HIGHLIGHT_THRESHOLD) {
      const fill = dominantCount / windowSize;  // 0.0 to 1.0
      setActiveDirection(dominantDirection);
      setFillLevel(Math.min(fill, 1.0));
    } else {
      setActiveDirection(null);
      setFillLevel(0);
    }

    // ── Fire selection if threshold is reached ────────────────────────────
    // Threshold check: dominant direction must appear in >= THRESHOLD fraction of the
    // FULL window size (WINDOW_SIZE=8), not just frames collected so far.
    // Using window.length as denominator lets 2/2 frames (100%) fire immediately —
    // dividing by WINDOW_SIZE requires ceil(0.70 * 8) = 6 agreeing frames to fire.
    if (
      dominantDirection !== null &&
      windowFilled &&
      dominantCount / windowSize >= CONFIDENCE_THRESHOLD
    ) {
      console.log(
        `[GazeNavigation] Selection fired: ${dominantDirection} ` +
        `(${dominantCount}/${window.length} frames, conf=${confidence?.toFixed(2)})`
      );

      // Record when the selection fired (for cooldown tracking)
      lastFiredRef.current = now;

      // Reset the window so the next selection starts fresh
      windowRef.current = [];
      setActiveDirection(null);
      setFillLevel(0);

      // Call the parent's callback with the selected direction.
      // The parent (HomePage or ResponsePage) maps this to a quadrant and calls handleClick().
      if (onGazeSelectRef.current) {
        onGazeSelectRef.current(dominantDirection);
      }
    }

  }, [gazeDirection, confidence, windowSize]);
  // Note: we intentionally omit onGazeSelect from deps (using ref instead)


  return {
    videoRef,           // ref for the hidden <video> element
    isReady,            // boolean: webcam + MediaPipe are running
    activeDirection,    // string | null: direction currently accumulating
    fillLevel,          // float 0.0–1.0: how close to triggering (for progress UI)
    irisVerticalRatio,  // float: current IVR (pass-through for calibration screens)
    doubleBlinkCount,   // integer: increments each time a double blink is detected
    error,              // string | null
  };
}


// ─────────────────────────────────────────────────────────────────────────────
// Direction → quadrant index mapping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a gaze direction string to the index of the corresponding UI quadrant.
 *
 * The Eye2Voice UI quadrant layout (matching HomePage.js and ResponsePage.js):
 *   items[0] → top    quadrant  → "up"    gaze
 *   items[1] → left   quadrant  → "left"  gaze
 *   items[2] → right  quadrant  → "right" gaze
 *   items[3] → bottom quadrant  → "down"  gaze
 *
 * Usage in HomePage.js:
 *   const idx = DIRECTION_TO_QUADRANT_INDEX[direction];
 *   if (idx !== undefined && items[idx]) handleClick(items[idx]);
 *
 * @param {string} direction  One of 'up', 'down', 'left', 'right'
 * @returns {number|undefined} Index into the items array, or undefined if direction is 'straight'
 */
export const DIRECTION_TO_QUADRANT_INDEX = {
  up:    0,  // top quadrant
  left:  1,  // left quadrant
  right: 2,  // right quadrant
  down:  3,  // bottom quadrant
  // 'straight' is intentionally not mapped (no quadrant to select)
};
